import './modal_link_sharer.less';

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import autobind from 'class-autobind';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import intersection from 'lodash/intersection';

import sendPostRequest from 'zoomforth_zfutils/send_post_request.js';

import Page from 'zoomforth_zf/models/page.js';
import Button from 'zoomforth_component_lib/btn_basic.jsx';
import TagsInput from 'zoomforth_component_lib/tags_input.jsx';
import BasicTextArea from 'zoomforth_component_lib/basic_textarea.jsx';
import TruncatedText from 'zoomforth_component_lib/truncated_text.jsx';
import CopyTextInput from 'zoomforth_component_lib/copy_text_input.jsx';
import SimpleModalWithHeader from 'zoomforth_component_lib/simple_modal_with_header.jsx';
import { AlertIcon, PageIcon, PlusIcon } from 'zoomforth_component_lib/app_modern_icons.jsx';
import PageAccessibilityDisplay from 'zoomforth_shared_management/components/page_accessibility_display.jsx';

import notificationSystemStore from 'zoomforth_zf/appglobals/notification-system.js';

import { 
  ShareableFacebookIcon,
  ShareableTwitterIcon,
  ShareableLinkedInIcon
} from 'zoomforth_shared_management/components/shareable_social_icons.jsx';

import userStore from 'zoomforth_zf/appglobals/user.js';
import routesByPageStore from 'zoomforth_zf/appglobals/routes-by-page.js';

class ModalLinkSharer extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      note: '',
      emails: props.initialEmails,
      emailError: null,
      isSubmitting: false,
      isEmailSharingOpen: !isEmpty(props.initialEmails)
    };

    autobind(this);
  }

  handleEmailInputRefAttach() {
    
  }

  render() {
    const {
      canCreateNewLink,
      isSimpleEmailSendingMode,
      page,
      urlToShare,
      onRequestClose,
      onViewAllLinksClick,
      onCreateNewLinkClick,
      onVisitorAccessSettingsView
    } = this.props;
    const {
      note,
      emails,
      emailError,
      isEmailSharingOpen,
      isSubmitting
    } = this.state;

    const isAuthorizedToAddEmails = userStore.get().getPermissions().canChangeVisitorAccessSettings(page);
    const { newEmails } = this.validateEmailsAgainstPagePermissions();

    const pageRoutes = routesByPageStore.get(page.id);

    return (
      <SimpleModalWithHeader 
        className="modal-link-sharer"
        title={
          <TruncatedText>
            Share Site <span className="title-extra"><PageIcon /> {page.get('name')}</span>
          </TruncatedText>
        }
        subtitle={
          <PageAccessibilityDisplay
            page={page}
            onVisitorAccessSettingsView={() => { onVisitorAccessSettingsView(page); }}
          />
        }
        onRequestClose={onRequestClose}
      >

        <div className="modal-section-title">
          URL To Share
        </div>

        <div className={classNames('link-sharer', { 'public clearfix': page.isPublic() })}>
          <div className="copy-field">
            <CopyTextInput
              value={urlToShare}
              focus={true}
              buttonContent="Copy"
            />
          </div>

          {
            (!isSimpleEmailSendingMode && page.isPublic()) &&
            <div className="social-sharing-actions">
              <ShareableFacebookIcon shareUrl={urlToShare}/>
              <ShareableTwitterIcon shareUrl={urlToShare}/>
              <ShareableLinkedInIcon shareUrl={urlToShare}/>
            </div>
          }
        </div>

        {
          !isSimpleEmailSendingMode &&
          <div className="new-link-container">
            {
              pageRoutes.length > 1 &&
                <span
                  className="view-all-links"
                  onClick={() => {
                    onViewAllLinksClick(page);
                  }}
                >
                  View all links
                </span>
            }

            {
              (canCreateNewLink && page.get('allow_new_links')) &&
                <span
                  className="create-new-link"
                  onClick={() => {
                    onCreateNewLinkClick(page);
                  }}
                >
                  <PlusIcon /> Create new link
                </span>
            }
          </div>
        }

        <div className="separator"></div>

        <div className="modal-section-title">
          Share Site Via Email
        </div>

        <div className="share-via-email">
          <TagsInput
            validationRegex={/\S+@\S+\.\S+/}
            onValidationReject={() => { this.setState({ emailError: 'Please enter a valid email address' }); }}
            value={emails}
            onChange={this.handleEmailListChange}
            inputProps={{
              onFocus:()=> { this.setState({ isEmailSharingOpen:true }); }
            }}
            placeholder="Email addresses..."
          />

          {
            emailError &&
            <div className="share-via-email-error">
              <AlertIcon /> { emailError }
            </div>
          }

          {
            (page.usesEmailPrivacy() && isAuthorizedToAddEmails && newEmails.length > 0) &&
            <div className="share-via-email-new-emails">
             <AlertIcon />  New email addresses will be added as approved visitors
            </div>
          }

          {
            isEmailSharingOpen &&
            <div>
              <BasicTextArea
                className="share-via-email-note"
                value={note}
                placeholder="Write a note..."
                onChange={(e) => {
                  this.setState({ note: e.target.value });
                }}
              />

              <Button
                className="share-via-email-btn"
                disabled={isEmpty(emails) || isSubmitting}
                onClick={this.handleSendEmailsClick}
              >
                Send invitation to site
              </Button>
            </div>
          }
        </div>

      </SimpleModalWithHeader>
    );
  }

  validateEmailsAgainstPagePermissions(emailsToCheck=null) {
    if (emailsToCheck === null) {
      emailsToCheck = [];
    }

    const pagePermissions = this.props.page.get('permissions');

    let newEmails = [];
    let validEmails = [];

    // Concatenate the current email state in order to return a true
    // representation of what was allowed and what wasn't
    const allEmails = emailsToCheck.concat(this.state.emails);

    // This can be null if they chose email auth but never added any emails or
    // domains. In this case every email is a new email.
    if (!pagePermissions) {
      return { validEmails, newEmails: allEmails };
    }

    for (let idx in allEmails) {
      const currentEmail = allEmails[idx];

      // Keep the email lists unique
      if (includes(validEmails.concat(newEmails), currentEmail)) {
        continue;
      }

      // Check if email matches an email in the permission list
      if (includes(pagePermissions.emails, currentEmail)) {
        validEmails.push(currentEmail);
        continue;
      }

      // Check if email domain matches a domain in the permission list
      if (includes(pagePermissions.domains, currentEmail.replace(/.*@/, ''))) {
        validEmails.push(currentEmail);
        continue;
      }

      // This email does not match an authorized email or have an authorized domain
      newEmails.push(currentEmail);
    }

    return { validEmails, newEmails };
  }

  handleEmailListChange(emails) {
    const isAuthorizedToAddEmails = userStore.get().getPermissions().canChangeVisitorAccessSettings(this.props.page);

    if (!this.props.page.usesEmailPrivacy()) {
      // If it doesn't require email auth then this page can be sent to anyone!
      this.setState({ emails: emails, emailError: null });

    } else if (!isAuthorizedToAddEmails) {
      const { validEmails, newEmails } = this.validateEmailsAgainstPagePermissions(emails);

      this.setState({
        emails: intersection(emails, validEmails),
        emailError: newEmails.length > 0 && `The following emails are not approved by the site admin: ${newEmails.join(', ')}`
      });

    } else {
      this.setState({ emails: emails, emailError: null });
    }
  }

  handleSendEmailsClick() {
    this.setState({ isSubmitting: true, emailError: null });

    const page = this.props.page;
    const { newEmails } = this.validateEmailsAgainstPagePermissions();

    if (page.usesEmailPrivacy()) {
      let permissions = page.getVisitorAccessEmailAuthWhitelist();
      permissions['emails'] = permissions.emails.concat(newEmails);

      page.save({ permissions: permissions }, { patch: true }).done(() => {
        this.handleSendEmails(newEmails.length > 0);
      });
    } else {
      this.handleSendEmails();
    }
  }

  handleSendEmails(hasNewEmails = false) {
    const { emails, note } = this.state;
    const { page, urlToShare, routeIdToShare, onRequestClose } = this.props;

    sendPostRequest(
      '/rpc/invites/invite-to-page',
      {
        emails: emails,
        page_link: urlToShare,
        route_id: routeIdToShare,
        page_id: page.get('id'),
        note: note
      },
      (responseJson) => {
        if (responseJson.success) {

          notificationSystemStore.get().addNotification({
            level:'success',
            message: (page.usesEmailPrivacy() && hasNewEmails) ? 'New visitors approved and invitation sent' : 'Email invitation sent'
          });          

          this.setState({
            emails: [],
            note: '',
            isSubmitting: false
          });
          onRequestClose();

        } else {
          notificationSystemStore.get().addNotification({
            level:'error',
            message: responseJson.error
          });
        }
      },
      () => {
        this.setState({
          emails: [],
          note: '',
          isSubmitting: false
        });

        onRequestClose();

        notificationSystemStore.get().addNotification({
          level:'error',
          message: 'An error occurred. Our engineers are investigating the problem. Please try again later.'
        });
      }
    );
  }

}

ModalLinkSharer.propTypes = {
  canCreateNewLink: PropTypes.bool,
  isSimpleEmailSendingMode: PropTypes.bool, // `true` if we want to reduce 
  initialEmails: PropTypes.arrayOf(PropTypes.string),
  page: PropTypes.instanceOf(Page).isRequired,
  urlToShare: PropTypes.string.isRequired,
  routeIdToShare: PropTypes.number.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  onVisitorAccessSettingsView: PropTypes.func.isRequired,
  onViewAllLinksClick: PropTypes.func.isRequired,
  onCreateNewLinkClick: PropTypes.func.isRequired
};

ModalLinkSharer.defaultProps = {
  canCreateNewLink: true,
  initialEmails: [],
  isSimpleEmailSendingMode: false
};

export default ModalLinkSharer;
